<template>
  <vl-layout>
    <PbsGoBackLink />
    <vl-grid>
      <vl-column width="12" width-xs="12">
        <vl-title tag-name="h1"> {{ $t('processtrigger.create.title') }} </vl-title>
      </vl-column>
    </vl-grid>
    <form @submit="onSubmit">
      <vl-grid>
        <vl-form-column v-vl-spacer:bottom.small>
          <PbsSelectField
            name="triggerType"
            :label="t('processtrigger.detail.triggerType')"
            :placeholder="$t('processtrigger.detail.triggerType')"
            :options="triggerTypes"
            :custom-label="enumerationLabel"
          />
        </vl-form-column>

        <vl-form-column v-vl-spacer:bottom.small>
          <PbsTextAreaField
            name="description"
            :label="t('processtrigger.detail.description')"
            :placeholder="t('processtrigger.detail.description')"
          />
        </vl-form-column>

        <vl-form-column v-if="triggerType" v-vl-spacer:bottom.small>
          <PbsSelectField
            name="typeTriggerType"
            :label="getTypeTriggerTypeDescription()"
            :placeholder="getTypeTriggerTypeDescription()"
            :options="isTimeTrigger ? timeTriggerTypes: deliveryProductTriggerTypes"
            :custom-label="enumerationLabel"
          />
        </vl-form-column>

        <vl-form-column v-if="triggerType && isTypeTriggerType('Levering')" v-vl-spacer:bottom.small>
          <PbsSelectField
            name="deliveryProcessDefinition"
            :label="t('processtrigger.detail.deliveryProcessDefinition')"
            :placeholder="$t('processtrigger.detail.deliveryProcessDefinitionDescription')"
            :options="filteredDeliveryProcessDefinitionOptions"
            :custom-label="(e) => e.label"
          />
        </vl-form-column>

        <vl-form-column v-if="triggerType && isTypeTriggerType('Product')" v-vl-spacer:bottom.small>
          <PbsSelectField
            name="productProcessDefinition"
            :label="t('processtrigger.detail.productProcessDefinition')"
            :placeholder="$t('processtrigger.detail.productProcessDefinitionDescription')"
            :options="filteredProductProcessDefinitionOptions"
            :custom-label="(e) => e.label"
          />
        </vl-form-column>

        <vl-form-column v-if="isTimeTrigger" v-vl-spacer:bottom.small>
          <PbsInputField
            name="schedule"
            :label="t('processtrigger.detail.schedule')"
            :placeholder="t('processtrigger.detail.schedule')"
          />
          <div v-if="!isInvalidCronSchedule && cronDescription">{{ cronDescription }}</div>
        </vl-form-column>

        <vl-form-column v-if="triggerType && isTypeTriggerType('Levering') && (isDeliveryTrigger || isProductTrigger)" v-vl-spacer:bottom.small>
          <PbsSelectField
            name="triggerDeliveryProcessDefinition"
            :label="t('processtrigger.detail.triggerDeliveryProcessDefinition')"
            :placeholder="$t('processtrigger.detail.triggerDeliveryProcessDefinition')"
            :options="filteredDeliveryProcessDefinitionOptions"
            :custom-label="(e) => e.label"
          />
        </vl-form-column>

        <vl-form-column v-if="triggerType && isTypeTriggerType('Product') && (isDeliveryTrigger || isProductTrigger)" v-vl-spacer:bottom.small>
          <PbsSelectField
            name="triggerProductProcessDefinition"
            :label="t('processtrigger.detail.triggerProductProcessDefinition')"
            :placeholder="$t('processtrigger.detail.triggerProductProcessDefinition')"
            :options="filteredProductProcessDefinitionOptions"
            :custom-label="(e) => e.label"
          />
        </vl-form-column>

        <vl-form-column v-if="isTimeTrigger" v-vl-spacer:bottom.small>
          <PbsSelectField
            name="triggerSource"
            :label="t('processtrigger.detail.triggerSource')"
            :placeholder="$t('processtrigger.detail.triggerSource')"
            :options="triggerSources"
            :custom-label="enumerationLabel"
          />
        </vl-form-column>

        <vl-form-column v-vl-spacer:bottom.small>
          <PbsRadioButtonField name="isEnabled" :label="$t('processtrigger.detail.isEnabled')" />
        </vl-form-column>
        <vl-action-group v-vl-spacer:bottom.small>
          <vl-button icon="save" :mod-loading="loading" mod-icon-before type="submit"
            >{{ $t('general.save') }}
          </vl-button>
        </vl-action-group>
      </vl-grid>
    </form>
  </vl-layout>
</template>
<script lang="ts" setup>
import { useForm } from 'vee-validate';
import { useI18n } from 'vue-i18n';
import { useProcessTriggerDetailStore } from '../../store/process-trigger/process-trigger-detail.store';
import { useRouter } from 'vue-router';
import { Ref, computed, onMounted, ref, watch } from 'vue';
import {
  EnumerationDto,
  DeliveryProcessDefinitionSearchResultDto,
  ProductProcessDefinitionSearchResultDto
} from '@/api/backoffice/clients';
import PbsRadioButtonField from '@/components/forms/PbsRadioButtonField.vue';
import PbsInputField from '@/components/forms/PbsInputField.vue';
import PbsTextAreaField from '@/components/forms/PbsTextAreaField.vue';
import PbsSelectField from '@/components/forms/PbsSelectField.vue';
import PbsGoBackLink from '@/components/PbsGoBackLink.vue';
import { useDeliveryProcessDefinitionListStore } from '../../store/delivery-process-definition/delivery-process-definition-list.store';
import { useProcessTriggerValidations, INVALID_CRON_SCHEDULE } from './process-trigger-validations';
import { ENUM_TimeTriggerTypes, ENUM_TriggerTypes, ENUM_TriggerSources, ENUM_DeliveryProductTriggerTypes } from '../../common';
import { useProductProcessDefinitionListStore } from '../../store/product-process-definition/product-process-definition-list.store';

const { t } = useI18n();
const store = useProcessTriggerDetailStore();
const deliveryProcessDefinitionStore = useDeliveryProcessDefinitionListStore();
const productProcessDefinitionStore = useProductProcessDefinitionListStore();
const router = useRouter();

const deliveryProcessDefinitionOptions: Ref<DeliveryProcessDefinitionSearchResultDto[]> = ref([]);
const productProcessDefinitionOptions: Ref<ProductProcessDefinitionSearchResultDto[]> = ref([]);

const filteredDeliveryProcessDefinitionOptions = computed(() => {
  return deliveryProcessDefinitionOptions.value.filter(
    (x) => [0, 1].includes(x.statusId)
  );
});

const filteredProductProcessDefinitionOptions = computed(() => {
  return productProcessDefinitionOptions.value.filter(
    (x) => [0, 1].includes(x.statusId)
  );
});

const { validationSchema, triggerTypeRef } = useProcessTriggerValidations(t);

const { handleSubmit, values, setFieldValue } = useForm({
  validationSchema
});

const triggerType = computed(() => {
  return values.triggerType;
});

const triggerTypes = computed((): EnumerationDto[] => {
  return Object.values(ENUM_TriggerTypes);
});

const timeTriggerTypes = computed((): EnumerationDto[] => {
  return Object.values(ENUM_TimeTriggerTypes);
});

const deliveryProductTriggerTypes = computed((): EnumerationDto[] => {
  let supportedTriggerTypes = [];
  if (isDeliveryTrigger.value) {
    supportedTriggerTypes = [ENUM_DeliveryProductTriggerTypes.Levering];
  } else if (isProductTrigger.value) {
    supportedTriggerTypes = [ENUM_DeliveryProductTriggerTypes.Product];
  }
  return Object.values(ENUM_DeliveryProductTriggerTypes).filter((e) => supportedTriggerTypes.includes(e));
});

const triggerSources = computed((): EnumerationDto[] => {
  return Object.values(ENUM_TriggerSources);
});

const isDeliveryTrigger = computed(() => {
  return values.triggerType?.id === 1;
});

const isProductTrigger = computed(() => {
  return values.triggerType?.id === 2;
});

const isTimeTrigger = computed(() => {
  return values.triggerType?.id === 3;
});

const getTypeTriggerTypeDescription = () => {
  if (isTimeTrigger.value) {
    return t('processtrigger.detail.timeTriggerType');
  } else if (isDeliveryTrigger.value) {
    return t('processtrigger.detail.deliveryTriggerType');
  } else if (isProductTrigger.value) {
    return t('processtrigger.detail.productTriggerType');
  }
};

const isTypeTriggerType = (type: string) => {
  return values.typeTriggerType?.name === type
};

watch(triggerType, (newValue, oldValue) => {
  triggerTypeRef.value = newValue;
  setFieldValue('typeTriggerType', null);
});

const schedule = computed(() => {
  return values.schedule;
});

watch(schedule, async (newValue, oldValue) => {
  if (newValue) {
    await store.getDescription(newValue);
  }
});

const isInvalidCronSchedule = computed(() => {
  return !values.validCronSchedule;
});

const cronDescription = computed(() => {
  return store.cronDescription;
});

watch(cronDescription, (newValue, oldValue) => {
  const validCronSchedule = newValue !== INVALID_CRON_SCHEDULE;
  setFieldValue('validCronSchedule', validCronSchedule);
  console.log('validCronSchedule', validCronSchedule);
});

const loading = computed((): boolean => {
  return store.loading;
});

const availableJobs = computed((): EnumerationDto[] => {
  return [ENUM_TimeTriggerTypes.ProductExportRunCleanupJob, ENUM_TimeTriggerTypes.SwitchJob];
});

onMounted(async () => {
  deliveryProcessDefinitionOptions.value = await deliveryProcessDefinitionStore.getAll();
  productProcessDefinitionOptions.value = await productProcessDefinitionStore.getAll();
});

const onSuccess = async (values) => {
  const typeTriggerType = values.typeTriggerType?.id;

  const job = availableJobs.value.find((e) => e.id === typeTriggerType);

  const id = await store.create({
    triggerType: values.triggerType.name,
    description: values.description,
    deliveryProcessDefinitionId: typeTriggerType == ENUM_TimeTriggerTypes.Levering.id ? values.deliveryProcessDefinition?.id : null,
    productProcessDefinitionId: typeTriggerType == ENUM_TimeTriggerTypes.Product.id ? values.productProcessDefinition?.id : null,
    job: job ? job.name : null,
    triggerDeliveryProcessDefinitionId: typeTriggerType == ENUM_TimeTriggerTypes.Levering.id ? values.triggerDeliveryProcessDefinition?.id : null,
    triggerProductProcessDefinitionId: typeTriggerType == ENUM_TimeTriggerTypes.Product.id ? values.triggerProductProcessDefinition?.id : null,
    triggerSource: values.triggerSource?.name,
    schedule: values.schedule,
    isEnabled: values.isEnabled
  });

  if (id) {
    router.push({
      name: 'processtrigger.detail',
      params: {
        id
      }
    });
  }
};

const onInvalidSubmit = ({ values, errors, results }) => {
  // console.log(values); // current form values
  // console.log(errors); // a map of field names and their first error message
  // console.log(results); // a detailed map of field names and their validation results
};

const onSubmit = handleSubmit(onSuccess, onInvalidSubmit);

const enumerationLabel = (enumerationDto: EnumerationDto) => {
  return `${enumerationDto.name}`;
};

defineExpose({ onSubmit });
</script>
